import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LocalStorageService } from 'src/app/common/services/local-storage.service';
import { timer, interval, lastValueFrom } from 'rxjs';
import { AuthRoutes } from 'src/app/auth/auth.routes';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { SecurityService } from './security.service';

@Injectable({ providedIn: 'root' })
export class SessionService {
  private window: Window;

  public $inactivityTimer;

  public $sessionTimer;

  public $userGroups;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private securityService: SecurityService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private recaptchaV3Service: ReCaptchaV3Service
  ) {
    this.window = this.document.defaultView;
  }

  initInactivityTimer() {
    if (this.window.onload != null) return;
    // console.log('::: initInactivityTimer :::');

    this.resetInactivityTimer();
    this.window.onload = () => this.resetInactivityTimer();
    this.window.addEventListener('scroll', () => this.resetInactivityTimer(), true);

    this.document.onmousemove = () => this.resetInactivityTimer();
    this.document.onkeydown = () => this.resetInactivityTimer();
    this.document.onclick = () => this.resetInactivityTimer();
  }

  initSessionTimer() {
    if (this.sessionTimerIsDefined()) return;
    // console.log('::: initSessionTimer :::');

    this.$sessionTimer = interval(60000).subscribe(async () => {
      const expireIn = this.localStorageService.get('sessionExpireIn');
      const sessionInfo = this.localStorageService.get('brokerSessionInfo');
      const expireInDate = new Date(expireIn * 1000);
      expireInDate.setMinutes(expireInDate.getMinutes() - 30);

      const currentTime = new Date().getTime();

      if (expireInDate.getTime() <= currentTime) {
        try {
          const captchaToken = await lastValueFrom(this.recaptchaV3Service.execute('reLogin'));
          await lastValueFrom(this.securityService.refreshToken(captchaToken, sessionInfo?.code || 'OLD'));
        } catch (error) {
          console.error(error);

          this.cleanUserSession();
          this.router.navigate([AuthRoutes.ROUTES.Login.fullPath]);
        }
      }
    });
  }

  private sessionTimerIsDefined() {
    return typeof this.$sessionTimer !== 'undefined' && this.$sessionTimer?.closed === false;
  }

  cleanUserSession() {
    this.window.onload = null;
    this.window.removeAllListeners('scroll');

    this.document.onmousemove = null;
    this.document.onkeydown = null;
    this.document.onclick = null;

    this.$sessionTimer?.unsubscribe();
    this.$inactivityTimer?.unsubscribe();
    this.securityService.removeStorageSession();
  }

  private resetInactivityTimer() {
    this.$inactivityTimer?.unsubscribe();
    this.$inactivityTimer = timer(1000 * 60 * 60).subscribe(async () => {
      try {
        await lastValueFrom(this.securityService.logout());
      } catch (error) {
        console.error(error);
      }

      this.cleanUserSession();
      this.router.navigate([AuthRoutes.ROUTES.Login.fullPath]);
    });
  }
}
